/**
 * スポット詳細のスタンプ判定ユーティリティ（#2540）。
 *
 * 1つのスポットに複数スタンプが紐づくケース（同時に有効な複数スタンプ）に対応するため、
 * 「1件に畳む」のではなく「期間内のスタンプ集合」を扱う。
 */

/** 期間・営業時間の判定に必要なスタンプのフィールド */
type PeriodLike = {
  startAt?: string | null
  endAt?: string | null
  open?: string | null
  close?: string | null
}

/** スタンプ固有の期間が未設定のときにフォールバックするキャンペーン押印期間 */
export type CampaignPeriod = {
  playingStartAt?: string | null
  playingEndAt?: string | null
}

/** HH:MM:SS を秒数に変換する。フォーマット不正（NaN 混入防止）の場合は null を返す */
const toSecs = (hms: string): number | null => {
  if (!/^\d{2}:\d{2}:\d{2}$/.test(hms)) return null
  const [h, m, s] = hms.split(':').map(Number)
  return h * 3600 + m * 60 + s
}

/**
 * 現在時刻が営業時間（open / close）内かを判定する。
 *
 * バックエンドの isWithinBusinessHours() に合わせた判定:
 * - 両方 null → 全日 OK
 * - 両方 '00:00:00' → 全日 OK（管理画面の「全日」設定）
 * - それ以外: '00:00:00' を 0 秒（深夜0時）として扱い、null は制限なしとして比較
 */
const isWithinBusinessHours = (stamp: PeriodLike, now: Date): boolean => {
  const open = stamp.open
  const close = stamp.close
  const isAllDayOk = (open == null && close == null) || (open === '00:00:00' && close === '00:00:00')
  if (isAllDayOk) return true

  const nowSecs = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
  // null → 制限なし、'00:00:00' → 0秒（深夜0時）として扱う
  const openSecs = open != null ? toSecs(open) : null
  const closeSecs = close != null ? toSecs(close) : null

  if (openSecs !== null && closeSecs !== null) {
    // 両方設定
    if (openSecs < closeSecs) {
      // 通常営業（例: 00:00:00〜18:00:00 や 09:00:00〜18:00:00）
      return nowSecs >= openSecs && nowSecs <= closeSecs
    }
    // 日跨ぎ（例: 22:00:00〜06:00:00）
    return !(nowSecs < openSecs && nowSecs > closeSecs)
  }
  if (openSecs !== null) {
    // open のみ設定（close なし = 制限なし）
    return nowSecs >= openSecs
  }
  if (closeSecs !== null) {
    // close のみ設定（open なし = 制限なし）
    return nowSecs <= closeSecs
  }
  return true
}

/**
 * 現在日時が、そのスタンプの押印期間（日付範囲）と営業時間（open/close）の両方に入っているかを判定する。
 * startAt / endAt が null の場合はキャンペーンの押印期間にフォールバックする。
 */
export function isStampInPlayablePeriod(stamp: PeriodLike, campaign: CampaignPeriod, now: Date): boolean {
  const startAt = stamp.startAt ?? campaign.playingStartAt
  const endAt = stamp.endAt ?? campaign.playingEndAt
  if (startAt != null && now < new Date(startAt)) return false
  if (endAt != null && now > new Date(endAt)) return false
  return isWithinBusinessHours(stamp, now)
}

/**
 * 現在「期間内」のスタンプを全件返す（順序は API の startAt 昇順を維持）。
 * 1件も期間内でない場合は空配列を返す（＝スポットとして期間外）。
 */
export function getPlayableStamps<T extends PeriodLike>(stamps: T[], campaign: CampaignPeriod, now: Date): T[] {
  return stamps.filter((stamp) => isStampInPlayablePeriod(stamp, campaign, now))
}

/**
 * 今回獲得を試みる「対象スタンプ」を1件決める。
 *
 * 優先順は「期間内かつ未獲得かつサーバー判定でも獲得可能」→「期間内かつ未獲得」→「期間内の先頭」→ null。
 * 同順位内では渡された順（= API の startAt 昇順）で最も早いものを選ぶ。
 *
 * 全 GPS の場合はバックエンドが残りを secondary で自動付与するため「どれを primary にしても同じ」だが、
 * 未獲得のものを優先することで secondary 自動付与が無い種別（QR 等）でも正しく1件ずつ進められる。
 *
 * `unavailableStampIds` にはサーバーが獲得不可と判定したスタンプ（曜日・祝日・1日1回制限など
 * クライアントでは判定できない条件を含む）を渡す。ボタンは「1件でも獲得可能なら活性」なので、
 * これを考慮しないと「活性なのに対象は獲得不可スタンプ」となり押下時にエラーダイアログになる。
 */
export function resolveTargetStamp<T extends PeriodLike & { id: number }>(
  stamps: T[],
  campaign: CampaignPeriod,
  acquiredStampIds: ReadonlySet<number>,
  now: Date,
  unavailableStampIds: ReadonlySet<number> = new Set(),
): T | null {
  const playable = getPlayableStamps(stamps, campaign, now)
  const notAcquired = playable.filter((stamp) => !acquiredStampIds.has(stamp.id))
  return notAcquired.find((stamp) => !unavailableStampIds.has(stamp.id)) ?? notAcquired[0] ?? playable[0] ?? null
}
